import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
  type ImgHTMLAttributes,
  type ReactNode
} from 'react';
import {
  motion,
  useReducedMotion,
  type TargetAndTransition
} from 'framer-motion';
import { X, ZoomIn } from 'lucide-react';
import {
  ImageLightboxContext,
  useImageLightbox,
  type ImageRect,
  type LightboxImage
} from './ImageLightboxContext';

interface ImageLightboxProviderProps {
  children: ReactNode;
}

interface ViewportSize {
  width: number;
  height: number;
}

const lightboxEase = [0.65, 0, 0.22, 1] as const;
const returnEase = [0.4, 0, 0.2, 1] as const;

function toMotionRect(rect: ImageRect): TargetAndTransition {
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height
  };
}

function fitImageToViewport(
  image: LightboxImage,
  viewport: ViewportSize
): ImageRect {
  const horizontalPadding = viewport.width < 640 ? 16 : 32;
  const maximumWidth = Math.max(1, viewport.width - horizontalPadding * 2);
  const maximumHeight = Math.max(1, viewport.height * 0.82);
  const viewportRatio = maximumWidth / maximumHeight;

  const width =
    image.aspectRatio > viewportRatio
      ? maximumWidth
      : maximumHeight * image.aspectRatio;
  const height = width / image.aspectRatio;

  return {
    top: (viewport.height - height) / 2,
    left: (viewport.width - width) / 2,
    width,
    height
  };
}

export function ImageLightboxProvider({
  children
}: ImageLightboxProviderProps) {
  const [activeImage, setActiveImage] = useState<LightboxImage | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [viewport, setViewport] = useState<ViewportSize>(() => ({
    width: window.innerWidth,
    height: window.innerHeight
  }));
  const reduceMotion = useReducedMotion();

  const openImage = useCallback((image: LightboxImage) => {
    setIsClosing(false);
    setViewport({ width: window.innerWidth, height: window.innerHeight });
    setActiveImage(image);
  }, []);

  const closeImage = useCallback(() => {
    if (reduceMotion) {
      setActiveImage(null);
      setIsClosing(false);
      return;
    }
    setIsClosing(true);
  }, [reduceMotion]);

  const contextValue = useMemo(
    () => ({ openImage, activeImageId: activeImage?.id ?? null }),
    [activeImage?.id, openImage]
  );
  const targetRect = activeImage
    ? fitImageToViewport(activeImage, viewport)
    : null;

  useEffect(() => {
    if (!activeImage) return undefined;

    const scrollPosition = window.scrollY;
    const body = document.body;
    const root = document.documentElement;
    const originalBodyStyles = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overscrollBehavior: body.style.overscrollBehavior
    };
    const originalRootOverflow = root.style.overflow;
    const originalRootScrollBehavior = root.style.scrollBehavior;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeImage();
    };
    const handleResize = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    };

    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollPosition}px`;
    body.style.width = '100%';
    body.style.overscrollBehavior = 'none';
    root.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleResize);

    return () => {
      body.style.overflow = originalBodyStyles.overflow;
      body.style.position = originalBodyStyles.position;
      body.style.top = originalBodyStyles.top;
      body.style.width = originalBodyStyles.width;
      body.style.overscrollBehavior = originalBodyStyles.overscrollBehavior;
      root.style.overflow = originalRootOverflow;
      root.style.scrollBehavior = 'auto';
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
      window.scrollTo(0, scrollPosition);
      root.style.scrollBehavior = originalRootScrollBehavior;
    };
  }, [activeImage, closeImage]);

  return (
    <ImageLightboxContext.Provider value={contextValue}>
      {children}
      {activeImage && targetRect && (
        <div
          className="fixed inset-0 z-[120]"
          role="dialog"
          aria-modal="true"
          aria-label={`Uvećan prikaz: ${activeImage.alt}`}
        >
          <motion.button
            type="button"
            className="absolute inset-0 cursor-zoom-out bg-black/84"
            aria-label="Zatvori uvećan prikaz"
            onClick={closeImage}
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: isClosing ? 0 : 1 }}
            transition={{
              duration: reduceMotion ? 0 : isClosing ? 0.36 : 0.24
            }}
          />

          <motion.img
            className="fixed z-10 rounded-[var(--radius-sm)] object-cover shadow-[0_24px_70px_rgba(0,0,0,0.45)]"
            src={activeImage.src}
            alt={activeImage.alt}
            decoding="sync"
            initial={
              reduceMotion
                ? toMotionRect(targetRect)
                : toMotionRect(activeImage.origin)
            }
            animate={toMotionRect(isClosing ? activeImage.origin : targetRect)}
            transition={{
              duration: reduceMotion ? 0 : isClosing ? 0.42 : 0.56,
              ease: isClosing ? returnEase : lightboxEase
            }}
            onAnimationComplete={() => {
              if (isClosing) {
                setActiveImage(null);
                setIsClosing(false);
              }
            }}
          />

          <motion.div
            className="fixed z-20"
            style={{
              top: targetRect.top,
              left: targetRect.left,
              width: targetRect.width,
              height: targetRect.height
            }}
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: isClosing ? 0 : 1 }}
            transition={{
              duration: reduceMotion ? 0 : 0.2,
              delay: isClosing ? 0 : 0.32
            }}
          >
            <button
              type="button"
              className="absolute right-3 top-3 grid size-11 cursor-pointer place-items-center rounded-[var(--radius-sm)] bg-white text-black transition-colors hover:bg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-black"
              onClick={closeImage}
              aria-label="Zatvori uvećan prikaz"
              autoFocus
            >
              <X aria-hidden="true" className="size-5" />
            </button>
            <p className="absolute left-0 top-full mt-3 max-w-[calc(100%-3.5rem)] text-sm text-white/75">
              {activeImage.alt}
            </p>
          </motion.div>
        </div>
      )}
    </ImageLightboxContext.Provider>
  );
}

interface ZoomableImageProps extends Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  'alt' | 'src'
> {
  alt: string;
  src: string;
  wrapperClassName?: string;
  lightboxId?: string;
  sources?: Array<{
    type: string;
    srcSet: string;
    sizes?: string;
  }>;
}

function createUnsplashSrcSet(src: string): string | undefined {
  if (!src.includes('images.unsplash.com') || !/[?&]w=\d+/.test(src)) {
    return undefined;
  }

  return [480, 640, 768, 960, 1280]
    .map(width => `${src.replace(/([?&])w=\d+/, `$1w=${width}`)} ${width}w`)
    .join(', ');
}

export function ZoomableImage({
  alt,
  src,
  className = '',
  wrapperClassName = '',
  lightboxId,
  sources,
  ...imageProps
}: ZoomableImageProps) {
  const generatedImageId = useId();
  const imageId = lightboxId ?? generatedImageId;
  const { openImage, activeImageId } = useImageLightbox();
  const isActive = activeImageId === imageId;
  const responsiveSrcSet = imageProps.srcSet ?? createUnsplashSrcSet(src);
  const responsiveSizes =
    imageProps.sizes ??
    (responsiveSrcSet
      ? '(min-width: 1024px) 50vw, (min-width: 640px) calc(100vw - 3rem), calc(100vw - 2rem)'
      : undefined);

  const handleOpen = (button: HTMLButtonElement) => {
    const image = button.querySelector('img');
    if (!image) return;
    const rect = image.getBoundingClientRect();
    const aspectRatio =
      image.naturalWidth > 0 && image.naturalHeight > 0
        ? image.naturalWidth / image.naturalHeight
        : rect.width / rect.height;

    openImage({
      id: imageId,
      src: image.currentSrc || src,
      alt,
      aspectRatio,
      origin: {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      }
    });
  };

  return (
    <button
      type="button"
      className={`group/image relative block w-full cursor-zoom-in overflow-hidden text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background ${wrapperClassName}`}
      onClick={event => handleOpen(event.currentTarget)}
      aria-label={`Otvori uvećanu fotografiju: ${alt}`}
    >
      <picture className="block h-full w-full">
        {sources?.map(source => (
          <source
            key={`${source.type}-${source.srcSet}`}
            type={source.type}
            srcSet={source.srcSet}
            sizes={source.sizes ?? responsiveSizes}
          />
        ))}
        <img
          {...imageProps}
          className={`${className} transition-transform duration-500 ease-out group-hover/image:scale-[1.015] ${isActive ? 'opacity-0' : ''}`}
          src={src}
          srcSet={responsiveSrcSet}
          sizes={responsiveSizes}
          alt={alt}
        />
      </picture>
      <span className="pointer-events-none absolute right-3 top-3 grid size-9 translate-y-1 place-items-center rounded-[var(--radius-sm)] bg-black/58 text-white opacity-0 transition-[opacity,transform] duration-200 group-hover/image:translate-y-0 group-hover/image:opacity-100 group-focus-visible/image:translate-y-0 group-focus-visible/image:opacity-100">
        <ZoomIn aria-hidden="true" className="size-4" />
      </span>
    </button>
  );
}
